export const getMaxCurveOffset = length => Math.max(0, length / 2);

export const clampCurveOffset = (offset, length) => {
  if (!Number.isFinite(offset)) {
    return 0;
  }
  const max = getMaxCurveOffset(length);
  return Math.max(-max, Math.min(max, offset));
};

export const getLegacyCurveOffset = (link, from, to) => {
  if (
    !Number.isFinite(link?.controlX) ||
    !Number.isFinite(link?.controlY) ||
    !from ||
    !to
  ) {
    return null;
  }
  const midX = (from.cx + to.cx) / 2;
  const midY = (from.cy + to.cy) / 2;
  const vx = to.cx - from.cx;
  const vy = to.cy - from.cy;
  const length = Math.hypot(vx, vy);
  if (length < 0.001) {
    return 0;
  }
  const nx = -vy / length;
  const ny = vx / length;
  return (link.controlX - midX) * nx + (link.controlY - midY) * ny;
};

export const getResolvedCurveOffset = (link, from, to) => {
  const vx = (to?.cx || 0) - (from?.cx || 0);
  const vy = (to?.cy || 0) - (from?.cy || 0);
  const length = Math.hypot(vx, vy);
  const rawCurveOffset = Number.isFinite(link?.curveOffset)
    ? link.curveOffset
    : getLegacyCurveOffset(link, from, to) || 0;
  return {
    length,
    curveOffset: clampCurveOffset(rawCurveOffset, length),
  };
};

export const getCurveOffsetFromCursor = (
  link,
  widgetRectById,
  cursorWorldX,
  cursorWorldY,
) => {
  const from = widgetRectById.get(link?.from);
  const to = widgetRectById.get(link?.to);
  if (!from || !to) {
    return null;
  }
  const midX = (from.cx + to.cx) / 2;
  const midY = (from.cy + to.cy) / 2;
  const vx = to.cx - from.cx;
  const vy = to.cy - from.cy;
  const length = Math.hypot(vx, vy);
  if (length < 0.001) {
    return 0;
  }
  const nx = -vy / length;
  const ny = vx / length;
  const px = cursorWorldX - midX;
  const py = cursorWorldY - midY;
  return clampCurveOffset(px * nx + py * ny, length);
};

const getAnchorOnRect = (rect, dirX, dirY, fallbackX, fallbackY) => {
  let vx = Number.isFinite(dirX) ? dirX : 0;
  let vy = Number.isFinite(dirY) ? dirY : 0;
  if (Math.abs(vx) < 0.001 && Math.abs(vy) < 0.001) {
    vx = Number.isFinite(fallbackX) ? fallbackX : 1;
    vy = Number.isFinite(fallbackY) ? fallbackY : 0;
  }
  const halfW = Math.max(1, rect.w / 2);
  const halfH = Math.max(1, rect.h / 2);
  const scaleX = Math.abs(vx) > 0.001 ? halfW / Math.abs(vx) : Infinity;
  const scaleY = Math.abs(vy) > 0.001 ? halfH / Math.abs(vy) : Infinity;
  const t = Math.min(scaleX, scaleY);
  return {
    x: rect.cx + vx * t,
    y: rect.cy + vy * t,
  };
};

export const getLinkGeometry = (link, widgetRectById, drawRenderMetrics) => {
  const from = widgetRectById.get(link?.from);
  const to = widgetRectById.get(link?.to);
  if (!from || !to) {
    return null;
  }
  const midX = (from.cx + to.cx) / 2;
  const midY = (from.cy + to.cy) / 2;
  const vx = to.cx - from.cx;
  const vy = to.cy - from.cy;
  const length = Math.hypot(vx, vy);
  const nx = length > 0.001 ? -vy / length : 0;
  const ny = length > 0.001 ? vx / length : 0;
  const { curveOffset } = getResolvedCurveOffset(link, from, to);
  const controlX = midX + nx * curveOffset;
  const controlY = midY + ny * curveOffset;

  const fromAnchor = getAnchorOnRect(
    from,
    controlX - from.cx,
    controlY - from.cy,
    to.cx - from.cx,
    to.cy - from.cy,
  );
  const toAnchor = getAnchorOnRect(
    to,
    controlX - to.cx,
    controlY - to.cy,
    from.cx - to.cx,
    from.cy - to.cy,
  );

  const sx = value => value + (drawRenderMetrics?.offsetX || 0);
  const sy = value => value + (drawRenderMetrics?.offsetY || 0);
  const path = `M ${sx(fromAnchor.x)} ${sy(fromAnchor.y)} Q ${sx(
    controlX,
  )} ${sy(controlY)} ${sx(toAnchor.x)} ${sy(toAnchor.y)}`;
  const handleX = sx(0.25 * fromAnchor.x + 0.5 * controlX + 0.25 * toAnchor.x);
  const handleY = sy(0.25 * fromAnchor.y + 0.5 * controlY + 0.25 * toAnchor.y);

  return {
    from,
    to,
    fromAnchor,
    toAnchor,
    controlX,
    controlY,
    curveOffset,
    path,
    handleX,
    handleY,
  };
};

export const migrateLegacyLinkCurve = (link, widgetRectById) => {
  if (!link) {
    return link;
  }
  if (Number.isFinite(link.curveOffset)) {
    return link;
  }
  if (!Number.isFinite(link.controlX) || !Number.isFinite(link.controlY)) {
    return link;
  }
  const from = widgetRectById.get(link.from);
  const to = widgetRectById.get(link.to);
  const offset = getLegacyCurveOffset(link, from, to);
  if (!Number.isFinite(offset)) {
    return link;
  }
  const { length } = getResolvedCurveOffset({ curveOffset: offset }, from, to);
  const { controlX, controlY, ...rest } = link;
  return { ...rest, curveOffset: clampCurveOffset(offset, length) };
};

export const expandLinkBounds = (bounds, link, widgetRectById) => {
  const geometry = getLinkGeometry(link, widgetRectById, {
    offsetX: 0,
    offsetY: 0,
  });
  if (!geometry) {
    return bounds;
  }
  return {
    minX: Math.min(
      bounds.minX,
      geometry.from.cx,
      geometry.to.cx,
      geometry.controlX,
    ),
    minY: Math.min(
      bounds.minY,
      geometry.from.cy,
      geometry.to.cy,
      geometry.controlY,
    ),
    maxX: Math.max(
      bounds.maxX,
      geometry.from.cx,
      geometry.to.cx,
      geometry.controlX,
    ),
    maxY: Math.max(
      bounds.maxY,
      geometry.from.cy,
      geometry.to.cy,
      geometry.controlY,
    ),
  };
};
